/**
 * Provider de NodeReal (MegaNode) para transacciones
 * Soporta BSC y otras redes usando sus Enhanced APIs (nr_getAssetTransfers)
 */

import { Transaction } from '@/types/transaction.types';
import { BaseTransactionProvider } from './base-provider';

interface NodeRealTransfer {
  blockNum: string;
  hash: string;
  from: string;
  to: string;
  value: string;
  asset: string;
  category: string;
  contractAddress?: string;
  tokenDecimal?: string;
}

export class NodeRealProvider extends BaseTransactionProvider {
  private baseUrl: string;

  constructor(network: string, apiKey: string, timeout = 15000) {
    super(network, timeout);

    // URL base de NodeReal para BSC Mainnet
    // Nota: Si se añaden otras redes de NodeReal, se debería mapear aquí.
    if (network === 'bsc') {
      this.baseUrl = `https://bsc-mainnet.nodereal.io/v1/${apiKey}`;
    } else if (network === 'polygon') {
      this.baseUrl = `https://polygon-mainnet.nodereal.io/v1/${apiKey}`;
    } else {
      this.baseUrl = `https://${network}-mainnet.nodereal.io/v1/${apiKey}`;
    }
  }

  async getTransactions(address: string, fromBlock?: number): Promise<Transaction[]> {
    try {
      console.log(`[NodeReal-${this.network}] Fetching transactions for ${address}`);

      // NodeReal nr_getAssetTransfers requiere llamadas separadas para origen y destino
      const [sentTransfers, receivedTransfers] = await Promise.all([
        this.getAssetTransfers(address, null, fromBlock),
        this.getAssetTransfers(null, address, fromBlock),
      ]);

      const allTransfers = [...sentTransfers, ...receivedTransfers];
      console.log(`[NodeReal-${this.network}] Raw transfers: sent=${sentTransfers.length}, received=${receivedTransfers.length}`);
      const transactions = await this.convertTransfers(allTransfers, address);

      console.log(`[NodeReal-${this.network}] Found ${transactions.length} transactions`);
      return transactions.sort((a, b) => b.blockNumber - a.blockNumber);
    } catch (error) {
      console.error(`[NodeReal-${this.network}] Error fetching transactions:`, error);
      return [];
    }
  }

  async getTokenTransfers(address: string, contractAddress?: string): Promise<Transaction[]> {
    try {
      console.log(`[NodeReal-${this.network}] Fetching token transfers for ${address}`);

      const [sentTransfers, receivedTransfers] = await Promise.all([
        this.getAssetTransfers(address, null, undefined, ['20'], contractAddress),
        this.getAssetTransfers(null, address, undefined, ['20'], contractAddress),
      ]);

      const allTransfers = [...sentTransfers, ...receivedTransfers];
      const transactions = await this.convertTransfers(allTransfers, address);

      return transactions.sort((a, b) => b.blockNumber - a.blockNumber);
    } catch (error) {
      console.error(`[NodeReal-${this.network}] Error fetching token transfers:`, error);
      return [];
    }
  }

  async getTransactionDetails(txHash: string): Promise<Transaction | null> {
    try {
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jsonrpc: '2.0',
          id: 1,
          method: 'eth_getTransactionByHash',
          params: [txHash],
        }),
        signal: AbortSignal.timeout(10_000),
      });

      const data = await response.json();
      const tx = data.result;
      if (!tx) return null;

      return {
        hash: tx.hash,
        from: tx.from,
        to: tx.to || '',
        value: this.parseValue(tx.value),
        timestamp: Math.floor(Date.now() / 1000),
        blockNumber: parseInt(tx.blockNumber, 16),
        status: 'confirmed',
        type: 'send',
        network: this.network,
        source: 'rpc',
      };
    } catch {
      return null;
    }
  }

  private async rpcCall(method: string, params: unknown[]): Promise<unknown> {
    const response = await fetch(this.baseUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ jsonrpc: '2.0', id: 1, method, params }),
      signal: AbortSignal.timeout(10_000),
    });
    const data = await response.json();
    if (data.error) throw new Error(data.error.message ?? 'NodeReal RPC error');
    return data.result;
  }

  private async getAssetTransfers(
    fromAddress: string | null,
    toAddress: string | null,
    fromBlock?: number,
    category: string[] = ['external', '20'],
    contractAddress?: string
  ): Promise<NodeRealTransfer[]> {
    try {
      const currentBlockHex = (await this.rpcCall('eth_blockNumber', [])) as string;
      const currentBlockNum = parseInt(currentBlockHex, 16);

      if (!currentBlockNum) return [];

      let startBlock = fromBlock ?? 0;

      const MAX_RANGE = 1999999;
      if (currentBlockNum - startBlock > MAX_RANGE) {
        startBlock = currentBlockNum - MAX_RANGE;
      }

      if (startBlock >= currentBlockNum) return [];

      const params: Record<string, unknown> = {
        category,
        pageSize: 100,
        fromBlock: `0x${startBlock.toString(16)}`,
        toBlock: currentBlockHex,
      };

      if (fromAddress) params.fromAddress = fromAddress;
      if (toAddress) params.toAddress = toAddress;
      if (contractAddress) params.contractAddress = contractAddress;

      const result = (await this.rpcCall('nr_getAssetTransfers', [params])) as {
        transfers?: NodeRealTransfer[];
      } | NodeRealTransfer[] | null;

      const transfersInResult = !Array.isArray(result) && result ? (result as { transfers?: NodeRealTransfer[] }).transfers : result as NodeRealTransfer[];
      console.log(`[NodeReal-${this.network}] nr_getAssetTransfers from=${params.fromBlock} to=${params.toBlock} from=${fromAddress ?? '-'} to=${toAddress ?? '-'} → ${Array.isArray(transfersInResult) ? transfersInResult.length : '?'} transfers`);

      if (result && !Array.isArray(result) && Array.isArray((result as { transfers?: NodeRealTransfer[] }).transfers)) {
        return (result as { transfers: NodeRealTransfer[] }).transfers;
      }
      return Array.isArray(result) ? result : [];
    } catch (error) {
      console.error(`[NodeReal-${this.network}] Error in getAssetTransfers:`, error);
      return [];
    }
  }

  private async convertTransfers(transfers: NodeRealTransfer[], userAddress: string): Promise<Transaction[]> {
    // Estimar timestamps a partir del número de bloque (BSC: ~3s por bloque)
    // Esto evita N llamadas individuales a eth_getBlockByNumber
    let currentBlockNum = 0;
    try {
      const currentBlockHex = (await this.rpcCall('eth_blockNumber', [])) as string;
      currentBlockNum = parseInt(currentBlockHex, 16);
    } catch { /* usa 0, los timestamps quedarán como Date.now() */ }

    const uniqueBlocks = [...new Set(transfers.map(t => t.blockNum))];
    const blockTimestamps: Record<string, number> = {};

    // Estimar timestamps sin llamadas RPC adicionales:
    // BSC produce un bloque cada ~3 segundos
    const BSC_BLOCK_TIME_MS = 3_000;
    const now = Date.now();
    for (const blockNumHex of uniqueBlocks) {
      const blockNum = parseInt(blockNumHex, 16);
      blockTimestamps[blockNumHex] = currentBlockNum > 0
        ? now - (currentBlockNum - blockNum) * BSC_BLOCK_TIME_MS
        : now;
    }

    return transfers.map(transfer => {
      const isToken = transfer.category === '20' || transfer.category === 'erc20';
      const decimals = transfer.tokenDecimal ? parseInt(transfer.tokenDecimal) : 18;
      const isOutgoing = transfer.from.toLowerCase() === userAddress.toLowerCase();
      const timestamp = blockTimestamps[transfer.blockNum] || Date.now();

      return {
        hash: transfer.hash,
        from: transfer.from,
        to: transfer.to,
        value: this.parseValue(transfer.value, decimals),
        timestamp,
        blockNumber: parseInt(transfer.blockNum, 16),
        status: 'confirmed',
        type: isOutgoing ? 'send' : 'receive',
        network: this.network,
        tokenAddress: isToken ? transfer.contractAddress : undefined,
        tokenSymbol: isToken ? transfer.asset : undefined,
        tokenDecimals: isToken ? decimals : undefined,
        source: 'rpc',
      };
    });
  }
}
